import { useCallback } from 'react'

import {
  BookOpen,
  Chrome,
  Command,
  ContactRound,
  ExternalLink,
  FileText,
  HelpCircle,
  Home,
  Info,
  LogOut,
  MessageCircleQuestion,
  RefreshCw,
  Rss,
  Scale,
  Settings,
  Telescope,
  WalletCards,
} from 'lucide-react'

import { FeedbackDialog } from '~/components/FeedbackDialog'
import { OrgSelector } from '~/components/Layout/OrgSelector'
import { PrivacyModeToggle } from '~/components/Layout/PrivacyModeToggle'
import { SmartAccountsSection } from '~/components/Layout/SmartAccountsSection'
import { useLastActivePath } from '~/components/Layout/useLastActivePath'
import { UserAvatar } from '~/components/UserAvatar'
import { SignInButton } from '~/components/auth/SignInButton'
import { useCommandMenu } from '~/components/command-menu'
import { useCommandMenuAvailability } from '~/components/command-menu/hooks/useCommandMenuAvailability'
import { Button, Dropdown } from '~/components/component-library'
import { FeatureCardSlot } from '~/components/feature-cards/FeatureCardSlot'
import { NotificationDotLabel, SidebarContainer } from '~/components/sidebar'
import {
  CHANGELOG_URL,
  DOCS_URL,
  getSplitsConnectInstallAttribution,
} from '~/constants/externalUrls'
import { useAuth } from '~/context/AuthProvider'
import { useOrgCompliance } from '~/hooks/compliance'
import { useActiveOrg } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useCanInstallSplitsConnect } from '~/hooks/useCanInstallSplitsConnect'
import { useFeatureCards } from '~/hooks/useFeatureCards'
import { useLogout } from '~/hooks/useLogout'
import { useShowExtensionsNotification } from '~/hooks/useShowExtensionsNotification'
import { cn } from '~/utils/tailwind-utils'
import { sendTrackingData } from '~/utils/tracking'

import type { DropdownEntry } from '~/components/component-library'

type SidebarItem = {
  prefix?: React.ReactElement
  title: React.ReactNode
  href: string
  hide?: boolean
  isActive?: boolean
}

export const AppSidebar = () => {
  const { user: authUser } = useAuthUser()
  const logout = useLogout()
  const { canViewPage: showCompliance } = useOrgCompliance()
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const { setActiveView } = useCommandMenu()
  const { isEnabled: isCommandMenuEnabled } = useCommandMenuAvailability()

  const effectivePathname = useLastActivePath()

  const openFeedbackDialog = useCallback(() => {
    setGlobalModalState({
      isOpen: true,
      content: <FeedbackDialog closeDialog={closeGlobalModal} />,
    })
  }, [setGlobalModalState, closeGlobalModal])

  const { featureFlags } = useActiveOrg()
  const showWatchedAccounts = !!featureFlags?.['watched-accounts']

  const { featureCards } = useFeatureCards()
  const canInstallExtension = useCanInstallSplitsConnect()
  const showExtensionsNotification = useShowExtensionsNotification()
  const installAttribution = getSplitsConnectInstallAttribution('helpMenu')
  /** Team sidebar items */
  const teamSidebarItems: SidebarItem[] = [
    {
      prefix: <Home height={12} width={12} />,
      title: 'Dashboard',
      isActive: effectivePathname === '/',
      href: '/',
    },
    {
      prefix: <WalletCards height={12} width={12} />,
      title: 'Accounts',
      // Active for /accounts and its sub-routes, except individual account
      // pages (/accounts/0x…) — those highlight their own entry in the
      // Accounts list below.
      isActive:
        effectivePathname.startsWith('/accounts') &&
        !/^\/accounts\/0x/i.test(effectivePathname),
      href: '/accounts',
    },
    {
      prefix: <ContactRound height={12} width={12} />,
      title: 'Contacts',
      isActive: effectivePathname.startsWith('/contacts'),
      href: '/contacts',
    },
    {
      prefix: <RefreshCw height={12} width={12} />,
      title: 'Schedules',
      isActive: effectivePathname.startsWith('/schedules'),
      href: '/schedules',
    },
    {
      prefix: <FileText height={12} width={12} />,
      title: 'Invoices',
      isActive: effectivePathname.startsWith('/invoices'),
      href: '/invoices',
    },
    {
      prefix: <Scale height={12} width={12} />,
      title: 'Compliance',
      isActive: effectivePathname.startsWith('/compliance'),
      href: '/compliance',
      hide: !showCompliance,
    },
    {
      prefix: <BookOpen height={12} width={12} />,
      title: 'Accounting',
      isActive: effectivePathname.startsWith('/accounting'),
      href: '/accounting',
    },
    {
      prefix: <Telescope height={12} width={12} />,
      title: 'Watched',
      isActive: effectivePathname.startsWith('/watched'),
      href: '/watched',
      hide: !showWatchedAccounts,
    },
    {
      prefix: <Settings height={12} width={12} />,
      title: (
        <NotificationDotLabel show={showExtensionsNotification}>
          Settings
        </NotificationDotLabel>
      ),
      isActive: effectivePathname.startsWith('/settings'),
      href: '/settings/team/general',
    },
  ]

  const helpMenuItems: DropdownEntry[] = [
    {
      icon: <MessageCircleQuestion />,
      content: 'Contact us…',
      onClick: openFeedbackDialog,
    },
    ...(isCommandMenuEnabled
      ? [
          {
            icon: <Command />,
            content: 'Command menu…',
            onClick: () => setActiveView('default'),
          },
        ]
      : []),
    {
      icon: <Info />,
      content: 'Docs',
      href: { path: DOCS_URL, newTab: true },
    },
    ...(canInstallExtension
      ? [
          {
            icon: <Chrome />,
            content: (
              <span className="flex flex-1 items-center justify-between gap-2">
                Install extension
                <ExternalLink className="size-3.5 shrink-0 text-secondary" />
              </span>
            ),
            href: {
              path: installAttribution.url,
              newTab: true,
            },
            onClick: () =>
              sendTrackingData(
                'clickedInstallSplitsConnect',
                installAttribution.eventProperties,
              ),
          },
        ]
      : []),
    // The 3 most recent feature cards, with the changelog folded in as the
    // section's last row (it replaced the old top-level Changelog item).
    // The divider only precedes a "What's new" section, so drop it with no cards.
    ...(featureCards.length > 0
      ? [
          'divider' as const,
          {
            content: (
              <span className="text-xs font-medium leading-tight text-primary">
                {"What's new"}
              </span>
            ),
            disabled: true,
          },
        ]
      : []),
    ...featureCards.slice(0, 3).map((card, index) => ({
      icon: <WhatsNewDot isNewest={index === 0} />,
      content: <span className="block max-w-36 truncate">{card.title}</span>,
      href: { path: card.learnMoreUrl, newTab: true },
    })),
    {
      icon: <Rss />,
      // With cards, this is the "What's new" footer; with none, it stands in
      // for the old top-level Changelog row (no regression).
      content: featureCards.length > 0 ? 'Full changelog' : 'Changelog',
      href: { path: CHANGELOG_URL, newTab: true },
    },
  ]

  // A single clickable profile row at the top (→ personal settings), then log
  // out — mirrors the inactive-networks dropdown's full-opacity header item.
  const userMenuItems: DropdownEntry[] = authUser
    ? [
        {
          content: (
            <div className="flex max-w-52 items-center gap-2.5">
              <UserAvatar user={authUser} size={32} />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium text-primary">
                  {authUser.displayName ?? authUser.email}
                </span>
                {!!authUser.displayName && (
                  <span className="truncate text-xs text-secondary">
                    {authUser.email}
                  </span>
                )}
              </div>
            </div>
          ),
          href: { path: '/settings/personal/profile' },
        },
        'divider',
        {
          icon: <LogOut />,
          content: 'Log out',
          onClick: logout,
        },
      ]
    : []

  return (
    <SidebarContainer>
      <div className="flex h-full w-full flex-col">
        {/* Scrollable region; the footer below stays pinned to the bottom. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="z-10 p-2">
            <OrgSelector />
          </div>
          <div className="flex w-full flex-col gap-0.5 px-2 py-1">
            {teamSidebarItems
              .filter((item) => !item.hide)
              .map((item) => (
                <Button
                  key={item.href}
                  variant="tertiary"
                  className={cn('w-full px-2.5 py-1.5 font-normal', {
                    'pointer-events-none bg-gray-200 dark:bg-gray-800':
                      item.isActive,
                  })}
                  alignLeft
                  prefix={item.prefix}
                  size="small"
                  href={{ path: item.href }}
                >
                  {item.title}
                </Button>
              ))}
          </div>
          {!!authUser && <SmartAccountsSection />}
          {!authUser && (
            <div className="w-full p-3">
              <SignInButton fluid />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 px-2 pb-2">
          {!!authUser && <FeatureCardSlot />}
          <div className="flex items-center">
            {!!authUser && (
              <Dropdown align="start" items={userMenuItems}>
                <Button
                  variant="tertiary"
                  size="small"
                  prefix={<UserAvatar user={authUser} size={20} />}
                />
              </Dropdown>
            )}
            <div className="ml-auto flex items-center">
              <PrivacyModeToggle />
              <Dropdown items={helpMenuItems} align="end">
                <Button
                  aria-label="Help"
                  variant="tertiary"
                  size="small"
                  prefix={
                    // 20px box matching the avatar trigger; the wrapper escapes
                    // Button's `[&>svg]:size-4` so the 16px glyph sets its own size.
                    <div className="flex size-5 items-center justify-center">
                      <HelpCircle size={16} />
                    </div>
                  }
                />
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
    </SidebarContainer>
  )
}

const WhatsNewDot = ({ isNewest }: { isNewest: boolean }) => (
  <span className="flex size-3.5 items-center justify-center">
    <span
      className={cn('size-[7px] rounded-full', {
        'bg-blue-500': isNewest,
        'bg-gray-300 dark:bg-gray-700': !isNewest,
      })}
    />
  </span>
)

export const SidebarLabel = ({
  title,
  children,
  className,
}: {
  title: string
  children?: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-1',
        className,
      )}
    >
      <label className="w-full flex-1 select-none border border-transparent px-2.5 py-1.5 text-sm font-normal text-secondary">
        {title}
      </label>
      {children}
    </div>
  )
}
